import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg, download, MONTHS_IT, pad, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, DownloadSimple, Printer, Trash, MagnifyingGlass } from "@phosphor-icons/react";

const n0 = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });
const n2 = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayStr = () => new Date().toISOString().slice(0, 10);

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-sm border border-border bg-card p-3" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-head font-bold tabular-nums">{value}</div>
  </div>
);

const LineTotals = ({ tot }) => (
  <div className="rounded-sm border border-border bg-card overflow-x-auto">
    <table className="w-full text-sm" data-testid="weights-line-totals">
      <thead className="bg-secondary"><tr>
        {["Linea", "Righe", "Pezzi", "Peso", "%"].map((h) => (
          <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
        ))}
      </tr></thead>
      <tbody>
        {(tot?.lines || []).map((l) => (
          <tr key={l.line} className="border-t border-border" data-testid={`line-total-${l.line}`}>
            <td className="px-3 py-1.5 font-semibold">{l.line}</td>
            <td className="px-3 py-1.5 tabular-nums">{l.count}</td>
            <td className="px-3 py-1.5 tabular-nums">{n0(l.pieces)}</td>
            <td className="px-3 py-1.5 tabular-nums">{n2(l.weight)}</td>
            <td className="px-3 py-1.5 tabular-nums">{l.percent}%</td>
          </tr>
        ))}
        <tr className="border-t-2 border-border bg-secondary/50 font-semibold">
          <td className="px-3 py-1.5">TOTALE</td>
          <td className="px-3 py-1.5 tabular-nums">{tot?.count || 0}</td>
          <td className="px-3 py-1.5 tabular-nums">{n0(tot?.pieces)}</td>
          <td className="px-3 py-1.5 tabular-nums">{n2(tot?.weight)}</td>
          <td className="px-3 py-1.5">100%</td>
        </tr>
      </tbody>
    </table>
  </div>
);

export default function Weights({ tab = "dashboard" }) {
  const { user } = useAuth();
  const isEmployee = user?.role === "employee";
  const canManage = ["admin", "manager"].includes(user?.role);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [day, setDay] = useState(todayStr());
  const [meta, setMeta] = useState({ lines: [], clients: [] });
  const [products, setProducts] = useState([]);
  const activeProducts = useMemo(() => products.filter((p) => p.active), [products]);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [line, setLine] = useState("all");
  const [tipo, setTipo] = useState("all");
  const [sistema, setSistema] = useState("all");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (isEmployee) return;
    api.get("/weights/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/weights/products").then((r) => setProducts(r.data)).catch(() => {});
  }, [isEmployee]);

  const load = useCallback(async () => {
    try {
      if (isEmployee) {
        const r = await api.get("/weights/my", { params: { year, month } });
        setData(r.data); setRows(r.data.records);
      } else if (tab === "dashboard") {
        setData((await api.get("/weights/dashboard")).data);
      } else if (tab === "entries") {
        const r = await api.get("/weights/day", { params: { day } });
        setData(r.data); setRows(r.data.rows);
      } else if (tab === "products") {
        setRows((await api.get("/weights/products", { params: { line, tipo, sistema, q } })).data);
      } else if (tab === "report") {
        setData((await api.get("/weights/days", { params: { year, month } })).data);
      }
    } catch (e) { toast.error(errMsg(e)); }
  }, [tab, isEmployee, year, month, day, line, tipo, sistema, q]);

  useEffect(() => { load(); }, [load]);

  const save = async (path, body, method = "post") => {
    try {
      await api[method](path, body);
      toast.success("Salvato");
      setForm(null);
      if (path.includes("products")) api.get("/weights/products").then((r) => setProducts(r.data)).catch(() => {});
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (path) => {
    try { await api.delete(path); toast.success("Eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const exp = (report, extra) => (
    <div className="flex gap-2">
      {["pdf", "xlsx", "csv"].map((f) => (
        <Button key={f} variant="outline" size="sm" className="rounded-sm" data-testid={`weights-export-${f}`}
          onClick={() => download(`/weights/export/${report}?fmt=${f}&${extra}`, `${report}.${f}`)
            .catch((e) => toast.error(errMsg(e)))}>
          {f === "pdf" ? <Printer size={14} className="mr-1.5" /> : <DownloadSimple size={14} className="mr-1.5" />}
          {f === "xlsx" ? "EXCEL" : f.toUpperCase()}
        </Button>
      ))}
    </div>
  );

  const MonthNav = () => (
    <div className="mobile-stack flex flex-wrap gap-2 items-center">
      <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
        <SelectTrigger className="h-9 w-[140px] rounded-sm" data-testid="weights-month"><SelectValue /></SelectTrigger>
        <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
        <SelectTrigger className="h-9 w-[105px] rounded-sm" data-testid="weights-year"><SelectValue /></SelectTrigger>
        <SelectContent>{[year - 2, year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  const TITLES = { dashboard: "Dashboard Pesi", entries: "Registro Pesate", products: "Lavorazioni", report: "Report Pesate" };
  const detail = rows.filter((r) => (line === "all" || r.line === line)
    && (!q || `${r.code} ${r.description} ${r.client}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-3" data-testid={`weights-${isEmployee ? "my" : tab}-page`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">{isEmployee ? "I miei pesi" : TITLES[tab]}</h1>
          <p className="text-sm text-muted-foreground mt-1">Gestione Pesi — pesate giornaliere per linea di lavorazione</p>
        </div>
        <div className="mobile-stack flex flex-wrap gap-2 items-center">
          {(isEmployee || tab === "report") && <MonthNav />}
          {!isEmployee && tab === "entries" && (
            <>
              <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
                className="h-9 w-[160px] rounded-sm" data-testid="weights-day" />
              {exp("giornaliero", `day=${day}&line=${line}`)}
              <Button size="sm" className="rounded-sm font-semibold" data-testid="weights-add-entry"
                onClick={() => setForm({ kind: "entry", date: day, line: "SUINO", div: 1, pieces: "", weight: "" })}>
                <Plus size={15} className="mr-1.5" /> Nuova pesata
              </Button>
            </>
          )}
          {!isEmployee && tab === "report" && (
            <>
              {exp("mensile", `year=${year}&month=${month}`)}
              <Button variant="outline" size="sm" className="rounded-sm" data-testid="report-operators-pdf"
                onClick={() => download(`/weights/export/disossatori?fmt=pdf&date_from=${year}-${pad(month)}-01&date_to=${year}-${pad(month)}-31`, `disossatori_${year}_${pad(month)}.pdf`)
                  .catch((e) => toast.error(errMsg(e)))}>
                <Printer size={14} className="mr-1.5" /> DISOSSATORI
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm" data-testid="report-lots-pdf"
                onClick={() => download(`/weights/export/lotti?fmt=pdf&year=${year}&month=${month}`, `lotti_${year}_${pad(month)}.pdf`)
                  .catch((e) => toast.error(errMsg(e)))}>
                <Printer size={14} className="mr-1.5" /> LOTTI
              </Button>
              {["SUINO", "BOVINO"].map((ln) => (
                <Button key={ln} variant="outline" size="sm" className="rounded-sm"
                  data-testid={`report-lots-xlsx-${ln.toLowerCase()}`}
                  onClick={() => download(`/weights/export/lotti?fmt=xlsx&year=${year}&month=${month}&line=${ln}`, `lotti_${ln}_${year}-${pad(month)}.xlsx`)
                    .catch((e) => toast.error(errMsg(e)))}>
                  <Printer size={14} className="mr-1.5" /> LOTTI {ln} EXCEL
                </Button>
              ))}
            </>
          )}
          {!isEmployee && tab === "products" && exp("lavorazioni", "")}
          {canManage && tab === "products" && (
            <Button size="sm" className="rounded-sm font-semibold" data-testid="weights-add-product"
              onClick={() => setForm({ kind: "product", line: "SUINO", unit: "kg", rate: "", active: true })}>
              <Plus size={15} className="mr-1.5" /> Nuova lavorazione
            </Button>
          )}
        </div>
      </div>

      {isEmployee && data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Peso mese" value={n2(data.weight)} testid="my-weight-total" />
            <Kpi label="Pezzi" value={n0(data.pieces)} testid="my-weight-pieces" />
            <Kpi label="Pesate" value={data.count} testid="my-weight-rows" />
          </div>
          <LineTotals tot={data} />
        </>
      )}

      {!isEmployee && tab === "dashboard" && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Peso oggi" value={n2(data.today.weight)} testid="kpi-today-weight" />
            <Kpi label="Pezzi oggi" value={n0(data.today.pieces)} testid="kpi-today-pieces" />
            <Kpi label="Peso mese" value={n2(data.month.weight)} testid="kpi-month-weight" />
            <Kpi label="Giornate lavorate" value={data.days_worked} testid="kpi-days" />
            <Kpi label="Lavorazioni attive" value={data.active_products} testid="kpi-products" />
          </div>
          <h2 className="font-head font-semibold pt-1">Totali per linea (mese corrente)</h2>
          <LineTotals tot={data.month} />
          <h2 className="font-head font-semibold pt-1">Top lavorazioni del mese</h2>
          <div className="rounded-sm border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="weights-top-products">
              <thead className="bg-secondary"><tr>
                {["Lavorazione", "Pezzi", "Peso"].map((h) => <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.top_products.map((p) => (
                  <tr key={p.description} className="border-t border-border">
                    <td className="px-3 py-1.5">{p.description}</td>
                    <td className="px-3 py-1.5 tabular-nums">{n0(p.pieces)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{n2(p.weight)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!isEmployee && tab === "entries" && data && (
        <>
          <LineTotals tot={data} />
          <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap gap-2 items-center">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sigla, descrizione, cliente..."
                className="h-9 w-64 pl-8 rounded-sm" data-testid="weights-search" />
            </div>
            <Select value={line} onValueChange={setLine}>
              <SelectTrigger className="h-9 w-[180px] rounded-sm" data-testid="weights-line-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le linee</SelectItem>
                {meta.lines.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {line !== "all" && exp("linea", `date_from=${day}&date_to=${day}&line=${line}`)}
          </div>
          <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
            <table className="w-full text-sm" data-testid="weights-table">
              <thead className="bg-secondary"><tr>
                {["", "Sigla", "Descrizione", "Cliente", "Pezzi", "Peso", "Div", "Linea", "Note"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {detail.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`weights-row-${r.id}`}>
                    <td className="px-3 py-1.5 text-left">
                      {canManage && (
                        <button className="text-muted-foreground hover:text-rose-500" data-testid={`weights-delete-${r.id}`}
                          onClick={() => remove(`/weights/entries/${r.id}`)}><Trash size={15} /></button>
                      )}
                    </td>
                    <td className="px-3 py-1.5 font-semibold cursor-pointer" onClick={() => setForm({ kind: "entry", ...r })}>{r.code || "—"}</td>
                    <td className="px-3 py-1.5 cursor-pointer" onClick={() => setForm({ kind: "entry", ...r })}>{r.description}</td>
                    <td className="px-3 py-1.5">{r.client || "—"}</td>
                    <td className="px-3 py-1.5 tabular-nums">{n0(r.pieces)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{n2(r.weight)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{r.div}</td>
                    <td className="px-3 py-1.5">{r.line}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.notes || ""}</td>
                  </tr>
                ))}
                {detail.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground" data-testid="weights-empty">Nessuna pesata per questa data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!isEmployee && tab === "products" && (
        <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap gap-2 items-center">
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sigla o descrizione..."
              className="h-9 w-60 pl-8 rounded-sm" data-testid="products-search" />
          </div>
          <Select value={line} onValueChange={setLine}>
            <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="products-line-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le linee</SelectItem>
              {meta.lines.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="h-9 w-[200px] rounded-sm" data-testid="products-tipo-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              {(meta.tipi || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sistema} onValueChange={setSistema}>
            <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="products-sistema-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i sistemi</SelectItem>
              {(meta.sistemi || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground ml-auto" data-testid="products-count">{rows.length} lavorazioni</span>
        </div>
      )}

      {!isEmployee && tab === "products" && (
        <div className="rounded-sm border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="weights-table">
            <thead className="bg-secondary"><tr>
              {["", "Sigla", "Descrizione", "Linea", "Tipo", "Sistema", "Attiva"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`weights-row-${r.id}`}>
                  <td className="px-3 py-1.5 text-left">
                    {user?.role === "admin" && (
                      <button className="text-muted-foreground hover:text-rose-500" data-testid={`weights-delete-${r.id}`}
                        onClick={() => remove(`/weights/products/${r.id}`)}><Trash size={15} /></button>
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-semibold cursor-pointer" onClick={() => canManage && setForm({ kind: "product", ...r })}>{r.code}</td>
                  <td className="px-3 py-1.5 cursor-pointer" onClick={() => canManage && setForm({ kind: "product", ...r })}>{r.name}</td>
                  <td className="px-3 py-1.5">{r.line}</td>
                  <td className="px-3 py-1.5 text-xs">{r.tipo || "—"}</td>
                  <td className="px-3 py-1.5 text-xs">{r.sistema || "—"}</td>
                  <td className="px-3 py-1.5">{r.active ? "Sì" : "No"}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground" data-testid="weights-empty">Nessuna lavorazione: creane una per iniziare</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!isEmployee && tab === "report" && data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Peso mese" value={n2(data.totals.weight)} testid="report-total-weight" />
            <Kpi label="Pezzi mese" value={n0(data.totals.pieces)} testid="report-total-pieces" />
            <Kpi label="Pesate" value={data.totals.count} testid="report-total-rows" />
          </div>
          <LineTotals tot={data.totals} />
          <h2 className="font-head font-semibold pt-1">Giornate</h2>
          <div className="rounded-sm border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="weights-days-table">
              <thead className="bg-secondary"><tr>
                {["Data", "Righe", "Pezzi", "Peso totale", "Linee", "Stampe"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {data.days.map((d) => (
                  <tr key={d.date} className="border-t border-border hover:bg-secondary/40" data-testid={`weights-day-${d.date}`}>
                    <td className="px-3 py-1.5 font-semibold">{itDate(d.date)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{d.count}</td>
                    <td className="px-3 py-1.5 tabular-nums">{n0(d.pieces)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{n2(d.weight)}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">
                      {d.lines.map((l) => `${l.line} ${n2(l.weight)} (${l.percent}%)`).join(" · ")}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <button className="text-xs font-semibold hover:underline mr-3" data-testid={`day-open-${d.date}`}
                        onClick={() => { setDay(d.date); window.location.href = "/pesi/registro"; }}>APRI</button>
                      <button className="text-muted-foreground hover:text-primary" title="Stampa report giornaliero"
                        data-testid={`day-print-${d.date}`}
                        onClick={() => download(`/weights/export/giornaliero?fmt=pdf&day=${d.date}`, `pesate_${d.date}.pdf`).catch((e) => toast.error(errMsg(e)))}>
                        <Printer size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {data.days.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground" data-testid="weights-empty">Nessuna pesata nel mese</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {form?.kind === "entry" && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="weights-dialog">
            <DialogHeader><DialogTitle className="font-head">{form.id ? "Modifica pesata" : "Nuova pesata"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Data</Label>
                <Input type="date" value={form.date ?? ""} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="wf-date" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Lavorazione</Label>
                <Select value={form.product_id || ""}
                  onValueChange={(v) => {
                    const p = products.find((x) => x.id === v);
                    setForm({ ...form, product_id: v, code: p?.code || form.code, description: p?.name || form.description, line: p?.line || form.line });
                  }}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="wf-product"><SelectValue placeholder="Seleziona (opzionale)" /></SelectTrigger>
                  <SelectContent className="max-h-56">
                    {activeProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}{p.line ? ` · ${p.line}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {[["code", "Sigla"], ["description", "Descrizione"], ["client", "Cliente"], ["pieces", "Pezzi"],
                ["weight", "Peso"], ["div", "Div"], ["notes", "Note"]].map(([k, l]) => (
                <div key={k} className={k === "description" || k === "notes" ? "sm:col-span-2" : ""}>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</Label>
                  <Input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="h-9 rounded-sm mt-1" data-testid={`wf-${k}`} />
                </div>
              ))}
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Linea</Label>
                <Select value={form.line} onValueChange={(v) => setForm({ ...form, line: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="wf-line"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.lines.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="weights-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" data-testid="weights-save"
                onClick={() => save(form.id ? `/weights/entries/${form.id}` : "/weights/entries", {
                  date: form.date, product_id: form.product_id || null, code: form.code || "",
                  description: form.description || "", client: form.client || "",
                  pieces: Number(form.pieces || 0), weight: Number(form.weight || 0),
                  div: Number(form.div || 1), line: form.line || "ALTRO", notes: form.notes || "",
                }, form.id ? "put" : "post")}>Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {form?.kind === "product" && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="weights-dialog">
            <DialogHeader><DialogTitle className="font-head">{form.id ? "Modifica lavorazione" : "Nuova lavorazione"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["code", "Sigla (es. PA, SL, MC)"], ["name", "Descrizione lavorazione"], ["tipo", "Tipo lavorazione"], ["sistema", "Sistema"], ["notes", "Note"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</Label>
                  <Input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="h-9 rounded-sm mt-1" data-testid={`wf-${k}`} />
                </div>
              ))}
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Linea</Label>
                <Select value={form.line} onValueChange={(v) => setForm({ ...form, line: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="wf-line"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta.lines.length ? meta.lines : ["SUINO"]).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Stato</Label>
                <Select value={form.active ? "1" : "0"} onValueChange={(v) => setForm({ ...form, active: v === "1" })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="wf-active"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="1">Attiva</SelectItem><SelectItem value="0">Non attiva</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="weights-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" data-testid="weights-save"
                onClick={() => save(form.id ? `/weights/products/${form.id}` : "/weights/products", {
                  code: form.code || "", name: form.name || "", line: form.line || "ALTRO",
                  tipo: form.tipo || "", sistema: form.sistema || "",
                  unit: form.unit || "kg", rate: Number(form.rate || 0), notes: form.notes || "",
                  active: !!form.active,
                }, form.id ? "put" : "post")}>Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
